/**
 * One day = one tab panel — the body revealed by the matching day tab in
 * `ItineraryDayTabs` (#245: the editor shows a single day at a time rather
 * than side-by-side columns). Renders its stops with the travel-time connector
 * pill BETWEEN consecutive stops (never before the first stop of the day —
 * D4 / spec "Travel-time surface") and owns the drag-drop + keyboard-reorder
 * gestures for its stops.
 *
 * Keyboard model (spec "Keyboard reorder / accessibility", WCAG 2.1.1):
 * Space/Enter grab, ↑/↓ move within day, ←/→ move across to an adjacent day,
 * Space/Enter drop, Esc cancel. Moves are reported up so the editor's single
 * ARIA live region can announce them. Note that ←/→ here move a *grabbed
 * stop* between days; ←/→ on the tab strip itself switches days, per the ARIA
 * tablist convention.
 */

import { useMemo } from 'react';
import type { SerializedDay } from './itineraryTypes';
import { ItineraryStopCard } from './ItineraryStopCard';
import { TravelTimePill } from './TravelTimePill';
import { Heading4, Caption } from '../ui/Typography';
import { Button } from '../ui/Button';
import { computeStopWarnings } from './stopTiming';

export interface GrabState {
  stopId: string;
  dayId: string;
}

interface ItineraryDayPanelProps {
  day: SerializedDay;
  dayIndex: number;
  dayCount: number;
  readOnly?: boolean;
  grab: GrabState | null;
  onSetGrab: (g: GrabState | null) => void;
  /** Keyboard move intent — direction within / across days. */
  onMove: (
    stopId: string,
    fromDayId: string,
    dir: 'up' | 'down' | 'left' | 'right'
  ) => void;
  onDropStop: (
    stopId: string,
    fromDayId: string,
    toDayId: string,
    toOrdinal: number
  ) => void;
  /** Open the winery picker for this day. */
  onAddVenue: () => void;
  /** Open the non-winery place picker for this day. */
  onAddPlace: () => void;
  onEditDayTitle: () => void;
  /** Open the day-date editor (set/clear). */
  onEditDayDate?: () => void;
  onRemoveDay: () => void;
  onEditStopDetails: (stopId: string) => void;
  onRemoveStop: (stopId: string) => void;
  /**
   * The other days, as "Move to day…" targets for each stop card — the
   * keyboard/power-user counterpart to dragging a stop onto a day tab (#245).
   */
  moveTargets?: { id: string; label: string }[];
  onMoveStopToDay?: (stopId: string, toDayId: string) => void;
}

export const ItineraryDayPanel = ({
  day,
  dayIndex,
  dayCount,
  readOnly = false,
  grab,
  onSetGrab,
  onMove,
  onDropStop,
  onAddVenue,
  onAddPlace,
  onEditDayTitle,
  onEditDayDate,
  onRemoveDay,
  onEditStopDetails,
  onRemoveStop,
  moveTargets,
  onMoveStopToDay,
}: ItineraryDayPanelProps) => {
  const dayLabel = day.title || `Day ${dayIndex + 1}`;
  // Memo so timing analysis only re-runs when this day's stops change.
  const warningsByStop = useMemo(
    () => computeStopWarnings(day.stops),
    [day.stops]
  );

  const handleStopKey = (e: React.KeyboardEvent, stopId: string) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (grab?.stopId === stopId) {
        onSetGrab(null); // drop
      } else {
        onSetGrab({ stopId, dayId: day._id });
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onSetGrab(null);
      return;
    }
    if (grab?.stopId !== stopId) return;
    const map: Record<string, 'up' | 'down' | 'left' | 'right' | undefined> = {
      ArrowUp: 'up',
      ArrowDown: 'down',
      ArrowLeft: 'left',
      ArrowRight: 'right',
    };
    const dir = map[e.key];
    if (dir) {
      e.preventDefault();
      onMove(stopId, day._id, dir);
    }
  };

  const onDragStart = (e: React.DragEvent, stopId: string) => {
    e.dataTransfer.setData('text/itinerary-stop', stopId);
    e.dataTransfer.setData('text/itinerary-from-day', day._id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('text/itinerary-stop')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    }
  };
  const onDropAt = (e: React.DragEvent, toOrdinal: number) => {
    e.preventDefault();
    const stopId = e.dataTransfer.getData('text/itinerary-stop');
    const fromDay = e.dataTransfer.getData('text/itinerary-from-day');
    if (stopId) onDropStop(stopId, fromDay, day._id, toOrdinal);
  };

  return (
    <section
      className="flex w-full flex-col rounded-lg border border-[#DBD4C9] bg-[#F2F0EA] p-3"
      aria-label={dayLabel}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-baseline gap-3 min-w-0">
          <Heading4>{dayLabel}</Heading4>
          {day.date ? (
            <button
              type="button"
              onClick={!readOnly && onEditDayDate ? onEditDayDate : undefined}
              disabled={readOnly || !onEditDayDate}
              className="font-sans text-xs uppercase tracking-widest text-stone-600 hover:text-primary-700 disabled:hover:text-stone-600 disabled:cursor-default"
              title="Edit date"
            >
              {(() => {
                const [y, m, d] = day.date.split('-').map((n) => parseInt(n, 10));
                const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
                return dt.toLocaleDateString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                });
              })()}
            </button>
          ) : !readOnly && onEditDayDate ? (
            <button
              type="button"
              onClick={onEditDayDate}
              className="text-xs italic text-stone-500 hover:text-primary-700"
            >
              + Set date
            </button>
          ) : null}
        </div>
        {!readOnly && (
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" onClick={onEditDayTitle}>
              Rename
            </Button>
            {dayCount > 1 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onRemoveDay}
                aria-label={`Remove ${dayLabel}`}
              >
                Remove
              </Button>
            )}
          </div>
        )}
      </div>

      {/* "No date" callout: only when at least one stop has timing data, so
          we don't pester users on empty/exploratory days. */}
      {!day.date &&
        day.stops.some((s) => s.arrivalTime || s.durationMinutes) && (
          <div className="mb-2 rounded-md border border-stone-200 bg-stone-50 px-3 py-1.5 text-[0.78rem] text-stone-600">
            No date set — venue-hours warnings are off for this day. Travel-time
            and overlap checks still run.
          </div>
        )}

      <div
        className="flex flex-1 flex-col gap-2"
        onDragOver={onDragOver}
        onDrop={(e) => onDropAt(e, day.stops.length)}
      >
        {day.stops.length === 0 && (
          <Caption className="rounded-lg border border-dashed border-[#DBD4C9] p-4 text-center text-gray-500">
            No stops yet.
          </Caption>
        )}

        {day.stops.map((stop, idx) => (
          <div key={stop._id}>
            {/* Travel pill BETWEEN stops only — never before the first. */}
            {idx > 0 && stop.travelToHere && (
              <TravelTimePill travel={stop.travelToHere} />
            )}
            <div onDrop={(e) => onDropAt(e, idx)} onDragOver={onDragOver}>
              <ItineraryStopCard
                stop={stop}
                position={idx + 1}
                total={day.stops.length}
                dayLabel={dayLabel}
                dayDate={day.date}
                warnings={warningsByStop[stop._id] ?? []}
                grabbed={grab?.stopId === stop._id}
                readOnly={readOnly}
                onGrabToggle={() =>
                  onSetGrab(
                    grab?.stopId === stop._id
                      ? null
                      : { stopId: stop._id, dayId: day._id }
                  )
                }
                onKeyboardMove={(e) => handleStopKey(e, stop._id)}
                onEditDetails={() => onEditStopDetails(stop._id)}
                onRemove={() => onRemoveStop(stop._id)}
                moveTargets={moveTargets}
                onMoveToDay={
                  onMoveStopToDay
                    ? (toDayId) => onMoveStopToDay(stop._id, toDayId)
                    : undefined
                }
                draggableProps={
                  readOnly
                    ? undefined
                    : {
                        draggable: true,
                        onDragStart: (e) => onDragStart(e, stop._id),
                        onDragOver,
                        onDrop: (e) => onDropAt(e, idx),
                        onDragEnd: () => undefined,
                      }
                }
              />
            </div>
          </div>
        ))}
      </div>

      {!readOnly && (
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="outline" fullWidth onClick={onAddVenue}>
            + Add winery
          </Button>
          <Button size="sm" variant="outline" fullWidth onClick={onAddPlace}>
            + Add place
          </Button>
        </div>
      )}
    </section>
  );
};
